// --- STEP 1: Imports and Setup ---
import React, { useState, useEffect } from "react";
import { Stepper, Step, StepLabel, Box, Typography } from "@mui/material";

import BasicInfoStep from "../events/steps/BasicInfoStep";
import LogisticsStep from "../events/steps/LogisticsStep";
import RsvpSettingsStep from "../events/steps/RsvpSettingsStep";
import MarketingContactStep from "../events/steps/MarketingContactStep";
import AdvancedStep from "../events/steps/AdvancedStep";

import ButtonComponent from "./ButtonComponent";

const steps = [
  "Basic Info",
  "Logistics",
  "RSVP Settings",
  "Marketing & Contact",
  "Advanced",
];

export default function EventFormStepperComponent({
  formData = {},
  setFormData,
  errors,
  setErrors,
  handleSubmit,
  loadingDescription,
  handleGenerateDescription,
  bannerPreview,
  uploadingBanner,
  handleFileChange,
  handleChange,
  spreadsheetError,
  handleBudgetFileUpload,
  handleClear,
  isEditMode,
  setShowDeleteDialog,
  showMicrositePrompt,
  setShowMicrositePrompt,
  activeStep,
  setActiveStep,
  handlePlaceSelect, // <-- IMPORTANT: passed from ManageEvents
}) {
  const [completed, setCompleted] = useState({});

  const totalSteps = () => steps.length;
  const isStepComplete = (step) => !!completed[step];

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleNext = () => {
    setCompleted((prev) => ({
      ...prev,
      [activeStep]: true,
    }));

    setActiveStep((prev) =>
      prev + 1 >= totalSteps() ? prev : prev + 1
    );
  };

  const handleBack = () => {
    setActiveStep((prev) => (prev - 1 < 0 ? 0 : prev - 1));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <BasicInfoStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            handleChange={handleChange}
            uploadingBanner={uploadingBanner}
            handleFileChange={handleFileChange}
          />
        );

      case 1:
        return (
          <LogisticsStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            spreadsheetError={spreadsheetError}
            handleBudgetFileUpload={handleBudgetFileUpload}
            handleChange={handleChange}
            handlePlaceSelect={handlePlaceSelect} // <-- pass through
          />
        );

      case 2:
        return (
          <RsvpSettingsStep
            formData={formData}
            setFormData={setFormData}
          />
        );

      case 3:
        return (
          <MarketingContactStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            bannerPreview={bannerPreview}
            uploadingBanner={uploadingBanner}
            handleFileChange={handleFileChange}
            handleChange={handleChange}
          />
        );

      case 4:
        return (
          <AdvancedStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            loadingDescription={loadingDescription}
            handleGenerateDescription={handleGenerateDescription}
            handleChange={handleChange}
          />
        );

      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  return (
    <Box>
      <Stepper nonLinear activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label, index) => (
          <Step key={label} completed={isStepComplete(index)}>
            <StepLabel onClick={handleStep(index)} sx={{ cursor: "pointer" }}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent(activeStep)}

      <Box display="flex" justifyContent="space-between" mt={4}>
        <ButtonComponent
          label="Back"
          type="secondary"
          onClick={handleBack}
          disabled={activeStep === 0}
        />

        {activeStep < steps.length - 1 ? (
          <ButtonComponent
            label="Next"
            type="primary"
            onClick={handleNext}
          />
        ) : (
          <ButtonComponent
            label={isEditMode ? "Update Event" : "Create Event"}
            type="primary"
            onClick={handleSubmit}
          />
        )}
      </Box>
    </Box>
  );
}